import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PlaceResponse } from '../../../models/orders/place.model';
import { PlaceService } from '../../../services/place.service';
import { SearchVehicleComponent } from '../../vehicle/search-vehicle/search-vehicle.component';
import { SearchClinicComponent } from '../../clinics/search-clinic/search-clinic.component';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { VehicleResponse } from '../../../models/vechicle.model';
import { ClinicResponse } from '../../../models/clinic.model';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-search-place',
  standalone: true,
  imports: [
        MatFormFieldModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        CommonModule,
        MatInputModule,
        SearchVehicleComponent,
        SearchClinicComponent,
        MatSelectModule,
        MatButtonModule,
        
  ],
  templateUrl: './search-place.component.html',
  styleUrl: './search-place.component.scss'
})
export class SearchPlaceComponent implements OnInit, OnDestroy {

  @Output() placeSelected = new EventEmitter<PlaceResponse>();
  isSaving: boolean = false;
  isLoading: boolean = false;

  placeTypeControl = new FormControl('', [Validators.required]);
  vehicleControl = new FormControl<VehicleResponse | null>(null);
  clinicControl = new FormControl<ClinicResponse | null>(null);
  customStringControl = new FormControl('');
  suggestions!: PlaceResponse[];
  private placeTypeSubscription: Subscription | undefined;

  placeForm: FormGroup = new FormGroup({
    placeType: this.placeTypeControl,
    vehicleId: this.vehicleControl,
    clinicId: this.clinicControl,
    customString: this.customStringControl,
  });

  onPlaceTypeSelected(event:any): void {
  }

  constructor(
    private placeService: PlaceService,
    private toastr: ToastrService,

  )
  {}

  ngOnInit(): void {
    this.placeTypeSubscription = this.placeTypeControl.valueChanges.subscribe(
      (placeType) => {
        this.updateConditionalValidators(placeType);
      }
    );
    // Set initial state
    this.updateConditionalValidators(this.placeTypeControl.value);
  }

  ngOnDestroy(): void {
    this.placeTypeSubscription?.unsubscribe();
  }


  updateConditionalValidators(placeType: string | null): void {
    // Get controls using the keys defined in fb.group
    const vehicle = this.vehicleControl;
    const clinic = this.clinicControl;
    const customString = this.customStringControl;

    // Ensure controls exist before modifying
    if (!vehicle || !clinic || !customString) {
        console.error("Form controls not found during validator update!");
        return;
    }

    // Clear existing validators first
    vehicle.clearValidators();
    clinic.clearValidators();
    customString.clearValidators();

    // Add validators based on the selected type
    if (placeType === 'Vehicle') {
      vehicle.setValidators([Validators.required]);
    } else if (placeType === 'Clinic') {
      clinic.setValidators([Validators.required]);
    } else if (placeType === 'Custom') {
      customString.setValidators([Validators.required, Validators.minLength(1)]); // e.g., require non-empty string
    }

    // Update the validity state AFTER setting validators
    vehicle.updateValueAndValidity({ emitEvent: false });
    clinic.updateValueAndValidity({ emitEvent: false });
    customString.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit(): void {

    for(const control in this.placeForm.controls) {
      if(this.placeForm.controls[control].invalid) {
        this.toastr.error("Please fill all required fields", "Error");
        return;
      }
    }
    this.placeForm.disable();

    let name = "";
    let objectId: string | undefined = undefined;

    if(this.placeTypeControl.value === 'Vehicle' && this.vehicleControl.value) {
      name = this.vehicleControl.value.name;
      objectId = this.vehicleControl.value.id;
    }
    else if(this.placeTypeControl.value === 'Clinic' && this.clinicControl.value) {
      name = this.clinicControl.value.name;
      objectId = this.clinicControl.value.id;
    }
    else if(this.placeTypeControl.value === 'Custom') {
      name = this.customStringControl.value ?? "";
    }
    else {
      console.error("No place type selected or invalid selection.");
      this.toastr.error("Something went wrong saving this place. Please reload the page and try again.");
      return;
    }

    const data = {
      name: name,
      objectId: objectId,
      type: this.placeTypeControl.value,
    }

    this.placeService.getOrCreatePlace(data).subscribe({
      next: (res: PlaceResponse) => {
        this.placeSelected.emit(res);
        this.isSaving = false;
        this.toastr.success("Place saved successfully", "Success");
      },
      error: (error) => {
        this.isSaving = false;
        this.placeForm.enable();
        this.toastr.error("Something went wrong saving this place. Please reload the page and try again.");
        console.error(error);
      }
    })
  }

}
